import {COUNTRY_CODES} from '@/utils/constants/countryConstants'

export const DEFAULT_LOCALE_LABEL = 'ES-US'

export const LOCALE_COLUMN_LABELS = {
  displayName_locale: 'Display Name',
  displaySize_locale: 'Display Size',
  productDescription_locale: 'Product Description',
  subCategory_locale: 'Subcategory',
}

export const getLocaleHeader = (columnKey, localeLabel) =>
  `${LOCALE_COLUMN_LABELS[columnKey]} (${localeLabel ?? DEFAULT_LOCALE_LABEL})`

const PRODUCT_COLUMN_DEFINITIONS = {
  productId: {header: 'Product ID', mandatory: true},
  status: {header: 'Status'},
  productName: {header: 'Product Name', mandatory: true},
  segmentCount: {header: 'Segment Count'},
  displayName_en: {
    header: 'Display Name (EN)',
    minSize: 350,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  subCategoryCount: {header: 'Subcategory Count'},
  sorting: {header: 'Sorting', editable: true, fieldType: 'text', inputType: 'integer'},
  showOnECommerce: {header: 'Show on E-Commerce', editable: true, fieldType: 'boolean', multiSearch: false},
  suggestedProductSellingPrice: {
    header: 'Suggested Product Selling Price',
    editable: true,
    fieldType: 'text',
    inputType: 'decimal',
    multiSearch: false,
  },
  multipleSellingUnits: {header: 'Multiple Selling Units', editable: true, fieldType: 'text', inputType: 'integer'},
  size: {header: 'Display Size (EN)', editable: true, fieldType: 'text'},
  displayCategory: {
    header: 'Display Category (EN)',
    minSize: 200,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  subCategory_en: {
    header: 'Subcategory (EN)',
    minSize: 200,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  displayBrand: {
    header: 'Display Brand (EN)',
    minSize: 200,
    editable: true,
    fieldType: 'text',
    mandatory: true,
  },
  keywords_en: {header: 'Keywords (EN)', minSize: 200, editable: true, fieldType: 'text'},
  minimumOrderQuantity: {header: 'Minimum Order Quantity', editable: true, fieldType: 'text', inputType: 'integer'},
  newSkuTag: {
    header: 'New SKU Tag',
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'boolean',
    multiSearch: false,
  },
  gtin: {header: 'GTIN', mandatory: true},
  segmentsToShow: {header: 'Segments to Show', editable: true, fieldType: 'segment'},
  displayName_locale: {
    header: getLocaleHeader('displayName_locale', DEFAULT_LOCALE_LABEL),
    editable: true,
    fieldType: 'text',
  },
  displaySize_locale: {
    header: getLocaleHeader('displaySize_locale', DEFAULT_LOCALE_LABEL),
    editable: true,
    fieldType: 'text',
  },
  productDescription_locale: {
    header: getLocaleHeader('productDescription_locale', DEFAULT_LOCALE_LABEL),
    editable: true,
    fieldType: 'text',
  },
  subCategory_locale: {
    header: getLocaleHeader('subCategory_locale', DEFAULT_LOCALE_LABEL),
    editable: true,
    fieldType: 'text',
  },
  productDescription_en: {header: 'Product Description (EN)', editable: true, fieldType: 'text'},
  displayCategoryId: {header: 'Display Category Id', editable: true, fieldType: 'text', mandatory: true},
  subCategoryId: {header: 'Subcategory Id', editable: true, fieldType: 'text', mandatory: true},
  displaySizeId: {header: 'Display Size ID', editable: true, fieldType: 'text', inputType: 'integer'},
  displayBrandId: {header: 'Display Brand Id', editable: true, fieldType: 'text', mandatory: true},
  sizeFormatId: {header: 'Size/Format Id', editable: true, fieldType: 'text'},
  sizeFormat_en: {header: 'Size/Format (EN)', editable: true, fieldType: 'text'},
  productTypeId: {header: 'Product Type Id', editable: true, fieldType: 'text'},
  productType_en: {header: 'Product Type (EN)', editable: true, fieldType: 'text'},
  routeTypeId: {header: 'Route Type ID'},
  imageAvailability: {header: 'Image Availability'},
  skuNumber: {header: 'SKU Number', minSize: 200, visibleForCountries: [COUNTRY_CODES.US]},
  upcNumber: {header: 'UPC Number', visibleForCountries: [COUNTRY_CODES.US]},
  newSkuStartDate: {
    header: 'New SKU Start Date',
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'date',
    filterVariant: 'date',
  },
  newSkuEndDate: {
    header: 'New SKU End Date',
    visibleForCountries: [COUNTRY_CODES.US],
    editable: true,
    fieldType: 'date',
    filterVariant: 'date',
  },
}

const buildProductListColumns = (orderedKeys, defaultVisibleKeys, overrides = {}) => {
  const visibleKeys = new Set(defaultVisibleKeys)
  return orderedKeys.map(columnKey => ({
    columnKey,
    ...PRODUCT_COLUMN_DEFINITIONS[columnKey],
    defaultVisible: visibleKeys.has(columnKey),
    ...overrides[columnKey],
  }))
}

const NA_PRODUCT_LIST_COLUMNS = buildProductListColumns(
  [
    'showOnECommerce',
    'sorting',
    'displayName_en',
    'productId',
    'skuNumber',
    'upcNumber',
    'size',
    'displayCategory',
    'subCategory_en',
    'displayBrand',
    'multipleSellingUnits',
    'keywords_en',
    'minimumOrderQuantity',
    'newSkuTag',
    'newSkuStartDate',
    'newSkuEndDate',
    'gtin',
    'segmentsToShow',
    'displayName_locale',
    'productName',
    'displaySize_locale',
    'productDescription_locale',
    'productDescription_en',
    'displayCategoryId',
    'subCategoryId',
    'displaySizeId',
    'displayBrandId',
    'sizeFormatId',
    'sizeFormat_en',
    'productTypeId',
    'productType_en',
    'suggestedProductSellingPrice',
  ],
  [
    'showOnECommerce',
    'sorting',
    'displayName_en',
    'productId',
    'skuNumber',
    'upcNumber',
    'size',
    'displayCategory',
    'subCategory_en',
    'displayBrand',
    'multipleSellingUnits',
    'keywords_en',
    'minimumOrderQuantity',
    'newSkuTag',
    'newSkuStartDate',
    'newSkuEndDate',
    'gtin',
    'segmentsToShow',
  ],
  {
    displayBrand: {mandatory: false},
    displayBrandId: {mandatory: false},
    displaySizeId: {mandatory: false},
    displayCategory: {mandatory: false},
    displayCategoryId: {mandatory: false},
    subCategoryId: {mandatory: false},
    subCategory_en: {mandatory: false},
    subCategory_locale: {mandatory: false},
  }
)

const PRODUCT_LIST_COLUMNS = buildProductListColumns(
  [
    'productId',
    'status',
    'productName',
    'segmentCount',
    'displayName_en',
    'subCategoryCount',
    'sorting',
    'showOnECommerce',
    'suggestedProductSellingPrice',
    'multipleSellingUnits',
    'size',
    'displayCategory',
    'subCategory_en',
    'displayBrand',
    'keywords_en',
    'minimumOrderQuantity',
    'newSkuTag',
    'gtin',
    'segmentsToShow',
    'displayName_locale',
    'displaySize_locale',
    'productDescription_locale',
    'subCategory_locale',
    'productDescription_en',
    'displayCategoryId',
    'subCategoryId',
    'displaySizeId',
    'displayBrandId',
    'sizeFormatId',
    'sizeFormat_en',
    'productTypeId',
    'productType_en',
    'routeTypeId',
    'imageAvailability',
  ],
  [
    'productId',
    'status',
    'productName',
    'segmentCount',
    'displayName_en',
    'subCategoryCount',
    'sorting',
    'showOnECommerce',
    'suggestedProductSellingPrice',
    'multipleSellingUnits',
  ],
  {size: {mandatory: true}}
)

export const getProductListColumnsForCountryCode = (countryCode = 'US') => {
  switch (countryCode) {
    case 'US':
      return NA_PRODUCT_LIST_COLUMNS
    default:
      return PRODUCT_LIST_COLUMNS
  }
}
